import { useState } from 'react'
import { MdClose, MdLocalOffer } from 'react-icons/md'

const formatBRL = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export interface AppliedCoupon {
    code: string
    /** Quanto o cupom abate, em centavos. */
    amount: number
    /** Já gravado no atendimento: não pode ser trocado nem removido por aqui. */
    persisted: boolean
}

interface CouponFieldProps {
    applied: AppliedCoupon | null
    /** Preço manual (com o desconto manual e o valor digitado), sem o cupom. */
    manualPrice: number
    applying: boolean
    onApply: (code: string) => void
    onRemove: () => void
}

/**
 * Cupom do agendamento (exame, consulta, procedimento).
 *
 * Soma ao desconto manual e passa por cima do teto do catálogo e do markup mínimo: é o
 * crédito do cliente na empresa. O valor mostrado aqui é uma prévia; o definitivo é
 * calculado pelo backend ao salvar. Só aparece para quem lê cupons — a página decide.
 */
export default function CouponField({ applied, manualPrice, applying, onApply, onRemove }: CouponFieldProps) {
    const [code, setCode] = useState('')
    const charged = Math.max(0, manualPrice - Math.min(applied?.amount ?? 0, manualPrice))

    return (
        <div className="bg-base-100 rounded-2xl border border-base-300/60 p-5 flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <MdLocalOffer className="text-lg text-secondary" />
                <h3 className="font-semibold">Cupom</h3>
            </div>

            {applied ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-success/50 bg-success/10 px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-semibold">
                            {applied.code}
                            <span className="ml-2 font-normal text-base-content/70">
                                -{formatBRL(applied.amount)}
                            </span>
                        </span>
                        <span className="text-xs text-base-content/70">
                            Total com o cupom: <span className="font-semibold text-base-content">{formatBRL(charged)}</span>
                            {applied.persisted ? ' · aplicado neste atendimento' : ' · aplicado ao salvar'}
                        </span>
                    </div>
                    {!applied.persisted && (
                        <button type="button" className="btn btn-ghost btn-sm gap-1" onClick={onRemove}>
                            <MdClose className="text-base" />
                            Remover
                        </button>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-1.5">
                    <label htmlFor="scheduling-coupon-code" className="text-sm font-medium">
                        Código do cupom
                    </label>
                    <div className="flex flex-wrap gap-2">
                        <input
                            id="scheduling-coupon-code"
                            type="text"
                            className="input input-bordered input-sm w-56 uppercase"
                            placeholder="Ex: CREDITO50"
                            value={code}
                            onChange={e => setCode(e.target.value.toUpperCase())}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && code.trim()) {
                                    e.preventDefault()
                                    onApply(code.trim())
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={!code.trim() || applying}
                            onClick={() => onApply(code.trim())}
                        >
                            {applying && <span className="loading loading-spinner loading-xs" />}
                            Aplicar
                        </button>
                    </div>
                    <p className="text-xs text-base-content/60">
                        Soma ao desconto e não depende do desconto máximo do item.
                    </p>
                </div>
            )}
        </div>
    )
}
